import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@library/components/sections/Accordion';
import { Button } from '@library/components/buttons';
import { ChannelButton } from '@bo-components/alerts/ChannelButton';
import type { FormattedChannel } from '@library/types/alerts';
import { PageTitle } from '@library/components/PageTitle';
import { Spinner } from '@library/components/Spinner';

export interface ChannelTreeProps {
  channels: Array<FormattedChannel> | undefined;
  setPage: (page: number) => void;
  selectedChannel: FormattedChannel | undefined;
  setChannels: (channels: Array<FormattedChannel> | undefined) => void;
  setSelectedChannel: (channel: FormattedChannel) => void;
  readChannel: (channel: FormattedChannel) => void;
}

export const ChannelTree = ({
  channels,
  setPage,
  selectedChannel,
  setChannels,
  setSelectedChannel,
  readChannel
}: ChannelTreeProps) => {
  const selectedChannelId = selectedChannel?.id;

  const areNotificationsVisibleSummary =
    selectedChannel?.displayName?.toLocaleLowerCase() === 'notifications';

  const handleChannelChange = (channel: FormattedChannel) => {
    setPage(1);
    setSelectedChannel(channel);
    readChannel(channel);

    const updatedChannels = channels?.map((c) =>
      c.id === channel.id ? { ...c, isChannelUnread: false } : c
    );

    setChannels(updatedChannels);
  };

  return (
    <div className="scrollbar-hidden flex w-1/4 flex-col overflow-scroll p-6">
      <PageTitle title="Alerts" subtitle="Game Operations" />
      <div className="flex flex-col gap-2 py-6">
        <Button
          className={`text-soClean-100 w-11/12 text-left text-sm ${
            areNotificationsVisibleSummary
              ? 'bg-stankonia-300 rounded-lg p-2'
              : 'p-2'
          } `}
          onClick={() =>
            handleChannelChange({
              description: '',
              displayName: 'Notifications',
              id: 0,
              isUserSubscribed: true,
              isChannelUnread: false
            })
          }
        >
          <div className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="stroke-soClean-170 mr-2 size-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
              />
            </svg>
            <p className="text-soClean-170"> My Notifications </p>
          </div>
        </Button>
        {channels && (selectedChannelId || selectedChannelId === 0) ? (
          <Accordion type="multiple">
            <AccordionItem value="subscribed-channels">
              <AccordionTrigger chevronPosition="left" labelAsTrigger={false}>
                <span className="flex flex-row">
                  <Button
                    type="button"
                    className="w-full text-start font-semibold"
                  >
                    Subscribed channels
                  </Button>
                  {!channels ||
                    (!selectedChannelId && selectedChannelId !== 0 && (
                      <Spinner />
                    ))}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <Accordion type="multiple" defaultValue={[]}>
                  {channels
                    .filter(
                      (channel: FormattedChannel) => channel.isUserSubscribed
                    )
                    .map((channel: FormattedChannel) => (
                      <ChannelButton
                        key={channel.id}
                        channel={channel}
                        selectedChannelId={selectedChannelId}
                        handleChannelChange={handleChannelChange}
                      />
                    ))}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="all-channels">
              <AccordionTrigger chevronPosition="left" labelAsTrigger={false}>
                <span className="flex flex-row">
                  <Button
                    type="button"
                    className="w-full text-start font-semibold"
                  >
                    All channels
                  </Button>
                  {!channels ||
                    (!selectedChannelId && selectedChannelId !== 0 && (
                      <Spinner />
                    ))}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <Accordion type="multiple" defaultValue={[]}>
                  {channels.map((channel: FormattedChannel) => {
                    return (
                      <ChannelButton
                        key={channel.id}
                        channel={channel}
                        selectedChannelId={selectedChannelId}
                        handleChannelChange={handleChannelChange}
                      />
                    );
                  })}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : (
          <Spinner className="my-4 ml-7" />
        )}
      </div>
    </div>
  );
};
